import { useState } from "react";
import { Search, Loader2, ScanLine, CheckCircle2, AlertCircle } from "lucide-react";
import { mcaLookupSearch, mcaLookupCin } from "@/lib/api";
import { toast } from "sonner";

/**
 * MCA Master-Data Lookup widget.
 * Two modes: search by name (typeahead) OR exact CIN lookup.
 * Calls parent `onPick(record)` with the normalised company record to pre-fill the form.
 */
export default function McaLookupWidget({ onPick }) {
  const [tab, setTab] = useState("name"); // 'name' | 'cin'
  const [q, setQ] = useState("");
  const [cin, setCin] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);

  const doSearch = async () => {
    if (q.trim().length < 3) {
      toast.error("Type at least 3 characters of the company name");
      return;
    }
    setSearching(true);
    setResults([]);
    try {
      const r = await mcaLookupSearch(q.trim());
      const items = r.data?.results || [];
      setResults(items);
      if (items.length === 0) {
        toast.message("No match in MCA dataset. Try the full registered name (UPPERCASE) or fall back to PDF upload.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setSearching(false);
    }
  };

  const doCinLookup = async () => {
    const v = cin.trim().toUpperCase();
    if (v.length < 15) {
      toast.error("CIN should be 21 characters (e.g. U12345MH2020PTC123456)");
      return;
    }
    setSearching(true);
    setResults([]);
    try {
      const r = await mcaLookupCin(v);
      setResults([r.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CIN not found");
    } finally {
      setSearching(false);
    }
  };

  const pickResult = (rec) => {
    setPicked(rec);
    onPick(rec);
    toast.success(`Pre-filled from MCA: ${rec.name}. Add directors manually below.`);
  };

  return (
    <div className="border border-zinc-200 bg-white mb-8" data-testid="mca-lookup-widget">
      <div className="border-b border-zinc-200 px-6 py-4 flex items-center gap-2">
        <ScanLine size={16} className="text-[#002FA7]" />
        <div className="label-eyebrow">Look up from MCA (data.gov.in)</div>
        <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">3.6M companies indexed</span>
      </div>

      <div className="p-6">
        <div className="flex border border-zinc-300 w-fit mb-4" data-testid="lookup-tabs">
          <button
            onClick={() => { setTab("name"); setResults([]); }}
            className={`px-4 py-2 text-sm font-medium ${tab === "name" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
            data-testid="lookup-tab-name"
          >
            By Name
          </button>
          <button
            onClick={() => { setTab("cin"); setResults([]); }}
            className={`px-4 py-2 text-sm font-medium border-l border-zinc-300 ${tab === "cin" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`}
            data-testid="lookup-tab-cin"
          >
            By CIN
          </button>
        </div>

        {tab === "name" ? (
          <div className="flex gap-2 flex-wrap">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Full registered name, e.g. CIRCUITNET TECHNOLOGIES INDIA PRIVATE LIMITED"
              className="flex-1 min-w-[280px] border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="lookup-name-input"
            />
            <button onClick={doSearch} disabled={searching} className="swiss-btn-primary" data-testid="lookup-name-btn">
              {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {searching ? "Searching…" : "Search"}
            </button>
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <input
              value={cin}
              onChange={(e) => setCin(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && doCinLookup()}
              placeholder="U12345MH2020PTC123456"
              maxLength={21}
              className="flex-1 min-w-[240px] border border-zinc-300 px-3 py-2 text-sm font-mono-plex focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="lookup-cin-input"
            />
            <button onClick={doCinLookup} disabled={searching} className="swiss-btn-primary" data-testid="lookup-cin-btn">
              {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {searching ? "Looking up…" : "Lookup CIN"}
            </button>
          </div>
        )}

        {tab === "name" && (
          <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
            Tip: data.gov.in stores company names in UPPERCASE — searches are exact-match. Use the full registered name as on MCA.
          </p>
        )}

        {results.length > 0 && (
          <ul className="mt-4 border border-zinc-200 divide-y divide-zinc-100" data-testid="lookup-results">
            {results.map((r) => {
              const isPicked = picked?.cin === r.cin;
              return (
                <li key={r.cin} className="p-3 flex items-start justify-between gap-3 hover:bg-zinc-50 transition-colors" data-testid={`lookup-result-${r.cin}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{r.name}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 flex gap-2 flex-wrap font-mono-plex">
                      <span>{r.cin}</span>
                      <span>·</span>
                      <span>{r.company_type}</span>
                      <span>·</span>
                      <span>{r.roc_code}</span>
                      <span>·</span>
                      <span className={r.company_status === "Active" ? "text-emerald-700" : "text-amber-700"}>{r.company_status}</span>
                    </div>
                    <div className="text-[11px] text-zinc-600 mt-1 line-clamp-1">{r.registered_address}</div>
                  </div>
                  <button
                    onClick={() => pickResult(r)}
                    className={isPicked ? "swiss-btn-ghost flex-shrink-0" : "swiss-btn-primary flex-shrink-0"}
                    data-testid={`pick-${r.cin}`}
                  >
                    {isPicked ? <CheckCircle2 size={14} /> : null}
                    {isPicked ? "Picked" : "Use This"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {picked && (
          <div className="mt-4 p-3 border border-amber-300 bg-amber-50 text-xs flex gap-2 items-start" data-testid="picked-warning">
            <AlertCircle size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Master data pre-filled.</strong> Directors are NOT included in the MCA open dataset — add them manually in the form below or upload a PDF for that part.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
